import { memo } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { listOf, TAG_LABEL } from '@/canvas/helpers';

// options arrive as plain strings or { v, label, hint }
export function normOptions(options) {
  return (Array.isArray(options) ? options : []).map((o) =>
    o !== null && typeof o === 'object'
      ? { v: String(o.v), label: String(o.label ?? o.v), hint: o.hint ? String(o.hint) : '' }
      : { v: String(o), label: String(o), hint: '' },
  );
}

const CHIP = 'inline-flex min-h-9 flex-col items-center justify-center rounded-lg border px-3 py-1 text-sm font-medium leading-tight transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60';
const CHIP_OFF = 'border-input bg-background text-foreground hover:bg-accent';
const CHIP_ON = 'border-primary bg-primary text-primary-foreground';

const TAG_ON = {
  M: 'border-(color:--tag-m) bg-(color:--tag-m-soft) text-(color:--tag-m)',
  E: 'border-(color:--tag-e) bg-(color:--tag-e-soft) text-(color:--tag-e)',
  G: 'border-(color:--tag-g) bg-(color:--tag-g-soft) text-(color:--tag-g)',
};

function Chips({ id, options, selected, multiple, disabled, invalid, onToggle, tagStyle }) {
  return (
    <div
      id={id}
      role={multiple ? 'group' : 'radiogroup'}
      aria-invalid={invalid || undefined}
      className={cn('flex flex-wrap gap-2 rounded-lg', invalid && 'ring-2 ring-destructive/40 ring-offset-2 ring-offset-background')}
    >
      {options.map((o) => {
        const on = selected.includes(o.v);
        return (
          <button
            key={o.v}
            type="button"
            role={multiple ? 'checkbox' : 'radio'}
            aria-checked={on}
            disabled={disabled}
            onClick={() => onToggle(o.v)}
            className={cn(CHIP, on ? (tagStyle ? TAG_ON[o.v] : CHIP_ON) : CHIP_OFF)}
          >
            <span>{o.label}</span>
            {o.hint && <span className={cn('text-[11px] font-normal', on ? 'opacity-90' : 'text-muted-foreground')}>{o.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}

function FieldControl({ field, fieldKey, domId, value, calcValue, disabled, invalid, onValue }) {
  const v = value ?? '';
  const id = domId || `fld-${fieldKey}`;

  if (field.kind === 'calc') {
    return (
      <div
        id={id}
        className="flex min-h-9 items-center rounded-md border border-dashed border-input bg-muted/60 px-3 py-1.5 text-sm font-semibold tabular-nums"
      >
        {calcValue ? calcValue : <span className="font-normal text-muted-foreground">Worked out for you</span>}
      </div>
    );
  }

  if (field.kind === 'choice') {
    const opts = normOptions(field.options);
    return (
      <Chips
        id={id}
        options={opts}
        selected={v ? [String(v)] : []}
        disabled={disabled}
        invalid={invalid}
        onToggle={(x) => onValue(fieldKey, String(v) === x ? '' : x)}
      />
    );
  }

  if (field.kind === 'tag') {
    const opts = normOptions(field.options).map((o) => ({ ...o, hint: TAG_LABEL[o.v] || '' }));
    return (
      <Chips
        id={id}
        options={opts}
        selected={v ? [String(v).toUpperCase()] : []}
        disabled={disabled}
        invalid={invalid}
        tagStyle
        onToggle={(x) => onValue(fieldKey, String(v).toUpperCase() === x ? '' : x)}
      />
    );
  }

  if (field.kind === 'multi') {
    const opts = normOptions(field.options);
    const chosen = listOf(v);
    return (
      <Chips
        id={id}
        options={opts}
        selected={chosen}
        multiple
        disabled={disabled}
        invalid={invalid}
        onToggle={(x) => {
          const set = new Set(chosen);
          if (set.has(x)) set.delete(x);
          else set.add(x);
          onValue(fieldKey, opts.filter((o) => set.has(o.v)).map((o) => o.v).join(','));
        }}
      />
    );
  }

  if (field.kind === 'number') {
    const prefix = field.prefix ? String(field.prefix) : '';
    const unit = field.unit ? String(field.unit) : '';
    return (
      <div className="relative">
        {prefix && <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">{prefix}</span>}
        <Input
          id={id}
          inputMode="decimal"
          autoComplete="off"
          value={v}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          placeholder={field.placeholder || '0'}
          className={cn('tabular-nums', prefix && 'pl-9', unit && 'pr-16')}
          onChange={(e) => onValue(fieldKey, e.target.value)}
        />
        {unit && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex max-w-[45%] items-center truncate text-xs text-muted-foreground">{unit}</span>
        )}
      </div>
    );
  }

  if (field.kind === 'long') {
    return (
      <Textarea
        id={id}
        rows={field.rows || 3}
        value={v}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        placeholder={field.placeholder || 'Write your answer'}
        onChange={(e) => onValue(fieldKey, e.target.value)}
      />
    );
  }

  return (
    <Input
      id={id}
      autoComplete="off"
      value={v}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      placeholder={field.placeholder || 'Type your answer'}
      onChange={(e) => onValue(fieldKey, e.target.value)}
    />
  );
}

export default memo(FieldControl);
